import React, { useState } from 'react';
import { GripVertical, Copy, Trash2, ChevronDown, ChevronUp, Plus, Image as ImageIcon, ArrowRight, ArrowLeft, CalendarDays } from 'lucide-react';
import { Card } from './SubscriptionAppCard';
import { Button } from './SubscriptionAppButton';
import { Input } from './SubscriptionAppInput';
import { CLUB_BUILDER_PRODUCTS, ProductSourceModal } from './ProductSourceModal';
import { FrequencyBillingDateSection } from './FrequencyBillingDateSection';
import { FrequencyRow } from './pack-sizes/FrequencyRow';
import { ReleaseCard } from './pack-sizes/ReleaseCard';
import { ProductSourceCard } from './pack-sizes/ProductSourceCard';
import { ReleaseScheduleSwitchDialog } from './pack-sizes/ReleaseScheduleSwitchDialog';
import { formatOrdinal, getBillingModeBadge } from '../utils/billingDates';
import { createRelease, formatReleasesSummary, getReleaseDates, keepPackProducts, sortReleases } from '../utils/releases';
// Types
export interface Frequency {
  id: string;
  name: string;
  interval: string;
  discount: string;
  settingsText: string;
  isCustomSettings?: boolean;
  hasExpandedSettings?: boolean;
  isRowExpanded?: boolean;
  loyaltyTier?: string;
  adHocDiscount?: string;
  pausedDiscount?: string;
}
export type BillingMode = 'rolling' | 'fixedDay' | 'releaseDates';
export interface ReleaseDate {
  month: number; // 1-12
  day: number; // 1-28
}
export type ReleaseDiscountType = 'percentage' | 'flat';
// One release on a pack billed on fixed release dates, used instead of frequencies
export interface PackRelease extends ReleaseDate {
  id: string;
  name: string; // optional, e.g. "Autumn/Winter"
  discountType: ReleaseDiscountType;
  discount: string; // percentage, e.g. "10"
  flatPrice: string; // dollars, e.g. "477"
  loyaltyTier?: string;
  adHocDiscount?: string;
  pausedDiscount?: string;
  products: Record<string, number>; // product id -> bottles, from the pack's Product Source
  isRowExpanded?: boolean;
}
export interface PackSize {
  id: string;
  number: number;
  title: string;
  subtitle: string;
  displayLabel: string;
  quantity: string;
  min: number;
  max: number;
  collectionName: string;
  productCount: number;
  // Products chosen in Product Source; on release dates, every product offered in any release
  productIds?: string[];
  frequencies: Frequency[];
  isExpanded: boolean;
  billingMode?: BillingMode;
  fixedDay?: number;
  // Sign-ups within this many days of a release count as that release
  cutoffDays?: number;
  releases?: PackRelease[];
}
// Add props interface
interface PackSizesPanelProps {
  boxType?: 'custom' | 'curated';
  onNext?: () => void;
  onBack?: () => void;
  packSizes: PackSize[];
  setPackSizes: React.Dispatch<React.SetStateAction<PackSize[]>>;
}
type PackBillingUpdates = Pick<Partial<PackSize>, 'billingMode' | 'fixedDay' | 'cutoffDays'>;
export function PackSizesPanel({
  boxType = 'curated',
  onNext,
  onBack,
  packSizes,
  setPackSizes
}: PackSizesPanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackId, setEditingPackId] = useState<string | null>(null);
  const [freqCustomTiers, setFreqCustomTiers] = useState<string[]>([]);
  const [pendingReleaseSwitchPackId, setPendingReleaseSwitchPackId] = useState<string | null>(null);
  const addCustomTier = (tier: string) => {
    setFreqCustomTiers(prev => prev.includes(tier) ? prev : [...prev, tier]);
  };
  const addPackSize = () => {
    const newNumber = packSizes.length + 1;
    const newId = `new-${Date.now()}`;
    const newPack: PackSize = {
      id: newId,
      number: newNumber,
      title: 'New Pack Size',
      subtitle: '0 products · 0 frequencies',
      displayLabel: '',
      quantity: '',
      min: 0,
      max: 0,
      collectionName: '',
      productCount: 0,
      frequencies: [],
      isExpanded: true
    };
    setPackSizes(prev => [...prev, newPack]);
  };
  const deletePackSize = (id: string) => {
    setPackSizes(prev => prev.filter(pack => pack.id !== id).map((pack, index) => ({
      ...pack,
      number: index + 1
    })));
  };
  const duplicatePackSize = (id: string) => {
    setPackSizes(prev => {
      const original = prev.find(pack => pack.id === id);
      if (!original) return prev;
      const newId = `dup-${Date.now()}`;
      const duplicate: PackSize = {
        ...original,
        id: newId,
        displayLabel: original.displayLabel ? `${original.displayLabel} - Copy` : 'Copy',
        title: original.title ? `${original.title} - Copy` : 'Copy',
        isExpanded: true,
        frequencies: original.frequencies.map(freq => ({
          ...freq,
          id: `${freq.id}-dup-${Date.now()}`
        })),
        releases: original.releases?.map(release => ({
          ...release,
          id: `${release.id}-dup-${Date.now()}`
        }))
      };
      const index = prev.findIndex(pack => pack.id === id);
      const newList = [...prev];
      newList.splice(index + 1, 0, duplicate);
      return newList.map((pack, i) => ({
        ...pack,
        number: i + 1
      }));
    });
  };
  const handleOpenProductSource = (packId: string) => {
    const pack = packSizes.find(p => p.id === packId);
    if (boxType === 'curated' && (!pack?.quantity || parseInt(pack.quantity) <= 0)) return;
    setEditingPackId(packId);
    setIsModalOpen(true);
  };
  const handleConfirmProductSource = (selectedIds: string[], quantities?: Record<string, number>) => {
    if (editingPackId) {
      const productCount = selectedIds.length;
      const isReleasePack = packSizes.find(p => p.id === editingPackId)?.billingMode === 'releaseDates';
      let subtitle = '';
      if (isReleasePack) {
        subtitle = `Manual Selection · ${productCount} products`;
      } else if (boxType === 'curated' && quantities) {
        const totalBottles = Object.values(quantities).reduce((a, b) => a + b, 0);
        subtitle = `Curated · ${productCount} products · ${totalBottles} bottles`;
      } else {
        subtitle = `Manual Selection · ${productCount} products · ${packSizes.find(p => p.id === editingPackId)?.frequencies.length || 0} frequencies`;
      }
      setPackSizes(prev => prev.map(pack => pack.id === editingPackId ? {
        ...pack,
        collectionName: boxType === 'curated' && !isReleasePack ? 'Curated Selection' : 'Manual Selection',
        productCount,
        productIds: selectedIds,
        subtitle,
        // Releases can only hold products from the pack's Product Source
        releases: pack.releases?.map(release => ({
          ...release,
          products: keepPackProducts(release.products, selectedIds)
        }))
      } : pack));
    }
    setIsModalOpen(false);
    setEditingPackId(null);
  };
  const togglePackSize = (id: string) => {
    setPackSizes(prev => prev.map(pack => pack.id === id ? {
      ...pack,
      isExpanded: !pack.isExpanded
    } : pack));
  };
  const addFrequency = (packId: string) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      const newFreq: Frequency = {
        id: `freq-${Date.now()}`,
        name: '',
        interval: '',
        discount: '',
        settingsText: 'Inherits defaults',
        isCustomSettings: false,
        hasExpandedSettings: true,
        isRowExpanded: true
      };
      return {
        ...pack,
        frequencies: [...pack.frequencies, newFreq]
      };
    }));
  };
  const deleteFrequency = (packId: string, freqId: string) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      return {
        ...pack,
        frequencies: pack.frequencies.filter(freq => freq.id !== freqId)
      };
    }));
  };
  const updateFrequencyField = (packId: string, freqId: string, updates: Partial<Frequency>) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      return {
        ...pack,
        frequencies: pack.frequencies.map(freq => freq.id === freqId ? {
          ...freq,
          ...updates
        } : freq)
      };
    }));
  };
  const duplicateFrequency = (packId: string, freqId: string) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      const original = pack.frequencies.find(f => f.id === freqId);
      if (!original) return pack;
      const duplicate: Frequency = {
        ...original,
        id: `freq-dup-${Date.now()}`
      };
      const index = pack.frequencies.findIndex(f => f.id === freqId);
      const newFreqs = [...pack.frequencies];
      newFreqs.splice(index + 1, 0, duplicate);
      return {
        ...pack,
        frequencies: newFreqs
      };
    }));
  };
  const toggleFrequencyRow = (packId: string, freqId: string) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      return {
        ...pack,
        frequencies: pack.frequencies.map(freq => freq.id === freqId ? {
          ...freq,
          isRowExpanded: !freq.isRowExpanded
        } : freq)
      };
    }));
  };
  const updatePackField = (packId: string, updates: Partial<PackSize>) => {
    setPackSizes(prev => prev.map(pack => pack.id === packId ? {
      ...pack,
      ...updates
    } : pack));
  };
  // Release cards stay sorted by date through the calendar year
  const updateReleases = (packId: string, change: (releases: PackRelease[]) => PackRelease[]) => {
    setPackSizes(prev => prev.map(pack => pack.id === packId ? {
      ...pack,
      releases: sortReleases(change(pack.releases ?? []))
    } : pack));
  };
  const addRelease = (packId: string) => {
    updateReleases(packId, releases => [...releases, createRelease(releases)]);
  };
  const updateRelease = (packId: string, releaseId: string, updates: Partial<PackRelease>) => {
    updateReleases(packId, releases => releases.map(release => release.id === releaseId ? {
      ...release,
      ...updates
    } : release));
  };
  const deleteRelease = (packId: string, releaseId: string) => {
    updateReleases(packId, releases => releases.length <= 1 ? releases : releases.filter(release => release.id !== releaseId));
  };
  // Switching to release dates replaces the frequency list, so confirm first if there is one
  const updatePackBilling = (packId: string, updates: PackBillingUpdates) => {
    const pack = packSizes.find(p => p.id === packId);
    if (!pack) return;
    const switchingToReleases = updates.billingMode === 'releaseDates' && pack.billingMode !== 'releaseDates';
    if (switchingToReleases && pack.frequencies.length > 0) {
      setPendingReleaseSwitchPackId(packId);
      return;
    }
    updatePackField(packId, switchingToReleases ? {
      ...updates,
      releases: pack.releases && pack.releases.length > 0 ? pack.releases : [createRelease([])]
    } : updates);
  };
  const pendingSwitchPack = packSizes.find(p => p.id === pendingReleaseSwitchPackId);
  const confirmReleaseSwitch = () => {
    if (!pendingSwitchPack) return;
    updatePackField(pendingSwitchPack.id, {
      billingMode: 'releaseDates',
      frequencies: [],
      releases: [createRelease([], pendingSwitchPack.frequencies[0])]
    });
    setPendingReleaseSwitchPackId(null);
  };
  const editingPack = packSizes.find(p => p.id === editingPackId);
  const isEditingReleasePack = editingPack?.billingMode === 'releaseDates';
  const packMin = editingPack ? editingPack.min : 0;
  const packMax = editingPack ? editingPack.max : 0;
  return <div className="flex flex-col gap-4 max-w-5xl mx-auto" data-id="element-4156" data-mp="xc62ijzb1sn2">
      <button className="w-full py-4 border-2 border-dashed border-[#CBD5E1] rounded-xl flex items-center justify-center gap-2 text-[14px] font-medium text-[#94A3B8] hover:border-[#0EA5E9] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] transition-colors duration-200" onClick={addPackSize} data-id="element-4157" data-mp="124h21lq4vcf3">
        <Plus className="w-4 h-4" data-id="element-4158" data-mp="eewesqmy5t00" />
        Add Pack Size
      </button>

      <ProductSourceModal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      setEditingPackId(null);
    }} onConfirm={handleConfirmProductSource} mode={isEditingReleasePack ? 'custom' : boxType} packMin={packMin} packMax={packMax} initialSelectedIds={editingPack?.productIds} data-id="element-4159" data-mp="egz6df3qzu6u" />

      <ReleaseScheduleSwitchDialog isOpen={Boolean(pendingSwitchPack)} frequencyCount={pendingSwitchPack?.frequencies.length ?? 0} firstFrequencyLabel={pendingSwitchPack?.frequencies[0] ? pendingSwitchPack.frequencies[0].interval || pendingSwitchPack.frequencies[0].name || undefined : undefined} onCancel={() => setPendingReleaseSwitchPackId(null)} onConfirm={confirmReleaseSwitch} data-id="element-4160" data-mp="2gac2hrjn8oll" />

      {packSizes.map(pack => {
      const billingMode = pack.billingMode ?? 'rolling';
      const fixedDay = pack.fixedDay ?? 1;
      const releases = sortReleases(pack.releases ?? []);
      const releaseDates = getReleaseDates(releases);
      const isReleaseMode = billingMode === 'releaseDates';
      const billingLabel = getBillingModeBadge(billingMode, fixedDay, releaseDates) ?? 'Rolling';
      const packProducts = CLUB_BUILDER_PRODUCTS.filter(product => (pack.productIds ?? []).includes(product.id));
      return <Card key={pack.id} padding="none" className="overflow-hidden border border-[#E2E8F0] shadow-sm" data-id="element-4161" data-mp="19hz61taq8c6k">
          <div className={`flex items-center justify-between px-4 py-4 cursor-pointer transition-colors ${pack.isExpanded ? 'bg-white border-b border-[#E2E8F0]' : 'bg-white hover:bg-[#F8FAFC]'}`} onClick={() => togglePackSize(pack.id)} data-id="element-4162" data-mp="1ehc3f7z851ne">
            <div className="flex items-center gap-4" data-id="element-4163" data-mp="1n8kpbj43eo4z">
              <div className="text-[#94A3B8] cursor-grab active:cursor-grabbing p-1 hover:bg-[#F1F5F9] rounded" data-id="element-4164" data-mp="1s8gxzvnqj3qv">
                <GripVertical className="w-5 h-5" data-id="element-4165" data-mp="1x70jjuyf2dip" />
              </div>
              <div className="w-7 h-7 rounded-full bg-[#0EA5E9] text-white text-[13px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-4166" data-mp="1x5pg5w8vv7s4">
                {pack.number}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3" data-id="element-4167" data-mp="2vvi8xasyd6j">
                <span className="text-[14px] font-bold text-[#0F172A]" data-id="element-4168" data-mp="7upzvvx19vp2">
                  {pack.displayLabel || pack.title}
                </span>
                <span className="hidden sm:inline text-[#CBD5E1]" data-id="element-4169" data-mp="1tbfmj2ln7gkc">•</span>
                <span className="text-[13px] text-[#64748B]" data-id="element-4170" data-mp="27af984plhldv">
                  {pack.productCount} products ·{' '}
                  {isReleaseMode ? formatReleasesSummary(releases) : `${pack.frequencies.length} frequencies · ${billingLabel}`}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-4171" data-mp="1akepwl7zvx3y">
              <button className="p-2 text-[#64748B] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] rounded-md transition-colors" onClick={e => {
              e.stopPropagation();
              duplicatePackSize(pack.id);
            }} title="Duplicate Pack & Frequencies" data-id="element-4172" data-mp="1alpuuod6fawn">
                <Copy className="w-4 h-4" data-id="element-4173" data-mp="vugw9praqf7d" />
              </button>
              <button className="p-2 text-[#64748B] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded-md transition-colors" onClick={e => {
              e.stopPropagation();
              deletePackSize(pack.id);
            }} title="Delete" data-id="element-4174" data-mp="qw5l9bp9lynk">
                <Trash2 className="w-4 h-4" data-id="element-4175" data-mp="37b98it8qsaia" />
              </button>
              <div className="w-px h-6 bg-[#E2E8F0] mx-1" data-id="element-4176" data-mp="2tj2sr89i45jd" />
              <button className="p-2 text-[#64748B] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-4177" data-mp="2tknnaxvtfoh8">
                {pack.isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-4178" data-mp="3m7i85vwjopv8" /> : <ChevronDown className="w-5 h-5" data-id="element-4179" data-mp="3m78e9x8dpyk9" />}
              </button>
            </div>
          </div>

          {pack.isExpanded && <div className="p-6 space-y-8 bg-white" data-id="element-4180" data-mp="3q8k0dss1o1np">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4181" data-mp="3c9u1c7lnzrwp">
                <div data-id="element-4182" data-mp="3cbom9lwxrk0s">
                  <Input label="Display Name" required value={pack.displayLabel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePackField(pack.id, {
                displayLabel: e.target.value
              })} placeholder="e.g. Six Pack" data-id="element-4183" data-mp="3h85bykjsa6fz" />
                  <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4184" data-mp="25ujn70hu66uz">
                    Shown to customers in the signup flow pack selector
                  </p>
                </div>
                {boxType === 'custom' ? <div data-id="element-4185" data-mp="1hrn3qp1sjfd9">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4186" data-mp="1cytxfwqobyz2">
                      Quantity <span className="text-[#EF4444]" data-id="element-4187" data-mp="17zz5o5aqb02t">*</span>
                    </label>
                    <input type="number" value={pack.quantity} onChange={e => updatePackField(pack.id, {
                quantity: e.target.value
              })} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-4188" data-mp="130mjlmrolx1a" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4189" data-mp="p26bbre67yh1">
                      Number of bottles the customer picks for this pack
                    </p>
                  </div> : <div data-id="element-4190" data-mp="2jemg0nrbj4js">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4191" data-mp="2jg78m7u2odf0">
                      Quantity <span className="text-[#EF4444]" data-id="element-4192" data-mp="2qxvq6j7nf79w">*</span>
                    </label>
                    <input type="number" value={pack.quantity} onChange={e => {
                const val = e.target.value;
                const numVal = parseInt(val) || 0;
                updatePackField(pack.id, {
                  quantity: val,
                  min: numVal,
                  max: numVal
                });
              }} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-4193" data-mp="2vw70ev1uvjsn" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4194" data-mp="2i038j8egfnjc">
                      Number of bottles in this curated pack. Product selections
                      must match this quantity.
                    </p>
                  </div>}
              </div>

              {boxType === 'custom' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4195" data-mp="1b661xhx6mr5">
                  <div data-id="element-4196" data-mp="nb52u4ny96sl">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4197" data-mp="n8iyedwcv7el">
                      Minimum Bottles
                    </label>
                    <input type="number" value={pack.min || ''} onChange={e => updatePackField(pack.id, {
                min: parseInt(e.target.value) || 0
              })} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-4198" data-mp="x1hv6j20a49a" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4199" data-mp="1fz0xz4xveqn9">
                      For custom boxes — minimum the customer must select
                    </p>
                  </div>
                  <div data-id="element-4200" data-mp="3hqnsvhkvv5p0">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4201" data-mp="3hpvvc17q6xw3">
                      Maximum Bottles
                    </label>
                    <input type="number" value={pack.max || ''} onChange={e => updatePackField(pack.id, {
                max: parseInt(e.target.value) || 0
              })} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-4202" data-mp="3ctxcsu92wa3l" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4203" data-mp="1nhij68f3ztw2">
                      Set higher than quantity to allow flexibility (e.g. 6–8)
                    </p>
                  </div>
                </div>}

              <div data-id="element-4204" data-mp="1dlyt42n87pf8">
                <h3 className="text-[14px] font-bold text-[#0F172A] mb-3" data-id="element-4205" data-mp="1dib0d5plv71k">
                  Billing date
                </h3>
                <FrequencyBillingDateSection idPrefix={pack.id} billingMode={billingMode} fixedDay={fixedDay} releaseDates={releaseDates} cutoffDays={pack.cutoffDays ?? 30} frequencies={pack.frequencies} onChange={updates => updatePackBilling(pack.id, updates)} data-id="element-4206" data-mp="14jfrtmz39kv3" />
              </div>

              <div data-id="element-4207" data-mp="6d0siwk38wus">
                <h3 className="text-[14px] font-bold text-[#0F172A] mb-3" data-id="element-4208" data-mp="6cimj87mj49o">
                  Product Source
                </h3>
                <ProductSourceCard isLocked={boxType === 'curated' && (!pack.quantity || parseInt(pack.quantity) <= 0)} collectionName={pack.collectionName || (boxType === 'curated' ? 'No products selected' : 'No collection selected')} productCount={pack.productCount} onChange={() => handleOpenProductSource(pack.id)} data-id="element-4209" data-mp="b521a8i1s5g2" />
                <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4210" data-mp="p15q1ma5jll5">
                  Search for products synced from your Shopify account
                </p>
                <div className="mt-3 flex items-start gap-3 bg-[#E0F2FE] border border-[#BAE6FD] rounded-lg px-4 py-3" data-id="element-4211" data-mp="g1ipf2p8yjt5">
                  <svg className="w-4 h-4 text-[#0EA5E9] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4212" data-mp="l03qt5g8j9j6">
                    <circle cx="12" cy="12" r="10" data-id="element-4213" data-mp="35rmen4f0xrn1" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" data-id="element-4214" data-mp="35t778ohs30i9" />
                  </svg>
                  <p className="text-[13px] text-[#0369A1] leading-relaxed" data-id="element-4215" data-mp="2xcq334t7ajb5">
                    Each pack size has its own collection. This lets you offer
                    exclusive products in larger packs — for example, museum
                    releases only available in the 12-pack.
                  </p>
                </div>
              </div>

              <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-4216" data-mp="2elfbelfni2i7">
                <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-4217" data-mp="29m2greijfr52">
                  <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-4218" data-mp="2o9xcz4vr8dmw">
                    Storefront Content
                  </span>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4219" data-mp="1vtw96gtgash8">
                  <div data-id="element-4220" data-mp="1vrtbdam23v1y">
                    <label className="block text-[13px] font-semibold text-[#0F172A] mb-2" data-id="element-4221" data-mp="1qv2wgqlzn5i4">
                      Pack Description
                    </label>
                    <textarea placeholder="e.g. Choose your own selection of six wines from our estate cellar." rows={4} className="w-full px-3 py-2.5 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-4222" data-mp="3qwspp0v0epom" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4223" data-mp="37yq6x3t21oz9">
                      Shown on the pack card in Step 1 of the signup flow
                    </p>
                  </div>
                  <div data-id="element-4224" data-mp="3gz4u68pkxe5e">
                    <label className="block text-[13px] font-semibold text-[#0F172A] mb-2" data-id="element-4225" data-mp="3gyljnd29ds1e">
                      Pack Image
                    </label>
                    <div className="flex items-start gap-4 border border-dashed border-[#CBD5E1] rounded-[6px] p-4 bg-[#F8FAFC]" data-id="element-4226" data-mp="3lvlib2ws3sih">
                      <div className="w-14 h-14 rounded-[6px] border border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] flex-shrink-0 bg-white" data-id="element-4227" data-mp="1177hdij8iuum">
                        <ImageIcon className="w-6 h-6" data-id="element-4228" data-mp="16ckrep8wu1pe" />
                      </div>
                      <div data-id="element-4229" data-mp="16enqdys1j569">
                        <p className="text-[13px] text-[#475569]" data-id="element-4230" data-mp="11lmb3pnx93m5">
                          <span className="font-semibold text-[#0F172A]" data-id="element-4231" data-mp="11jrl3wxsmlb7">
                            Upload image
                          </span>{' '}
                          or drag and drop
                        </p>
                        <p className="text-[12px] text-[#94A3B8] mt-0.5" data-id="element-4232" data-mp="nm34j1are0by">
                          Displayed on the pack selection card. Recommended 600
                          x 400px.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div data-id="element-4233" data-mp="2czzscbo66kt7">
                <div className="mb-4" data-id="element-4234" data-mp="2d0hnf4z3iszj">
                  <h3 className="text-[16px] font-bold text-[#0F172A]" data-id="element-4235" data-mp="28zfvl97idj7l">
                    {isReleaseMode ? 'Releases' : 'Frequencies, Pricing & Membership Tiers'}
                  </h3>
                  {isReleaseMode && <p className="text-[13px] text-[#64748B] mt-1" data-id="element-4236" data-mp="2l1gwszwmxo02">
                      Members are charged on each release date. Every release
                      has its own price, products and settings.
                    </p>}
                </div>
                {billingMode === 'fixedDay' && <p className="flex items-center gap-2 mb-3 text-[13px] text-[#475569]" data-id="element-4237" data-mp="2b5dsbneb9d9y">
                    <CalendarDays className="w-4 h-4 text-[#0EA5E9]" data-id="element-4238" data-mp="1xabgykqp2vpr" />
                    Every frequency on this pack bills on the{' '}
                    {formatOrdinal(fixedDay)}
                  </p>}
                {isReleaseMode ? <div className="space-y-3" data-id="element-4239" data-mp="1scs33lxwskyc">
                    {releases.map(release => <ReleaseCard key={release.id} idPrefix={pack.id} release={release} packProducts={packProducts} packMin={pack.min} packMax={pack.max} canDelete={releases.length > 1} customTiers={freqCustomTiers} onAddCustomTier={addCustomTier} onUpdate={updates => updateRelease(pack.id, release.id, updates)} onDelete={() => deleteRelease(pack.id, release.id)} data-id="element-4240" data-mp="1saywg6naq0qc" />)}
                    <button type="button" className="flex items-center gap-1.5 text-[13px] font-medium text-[#475569] border border-dashed border-[#CBD5E1] hover:border-[#0EA5E9] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] px-3 py-1.5 rounded-md transition-colors" onClick={() => addRelease(pack.id)} data-id="element-4241" data-mp="uss5w3xobmem">
                      <Plus className="w-3.5 h-3.5" data-id="element-4242" data-mp="1io4ng78wqb4h" />
                      Add release
                    </button>
                  </div> : <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-4243" data-mp="1dq2wl058p7ig">
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-4244" data-mp="1sepgzg9q4pgj">
                      <div className="col-span-3 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-4245" data-mp="1ni8x4y9k4x99">
                        Frequency
                      </div>
                      <div className="col-span-3 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-4246" data-mp="1wif5y6ub9923">
                        Interval
                      </div>
                      <div className="col-span-2 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-4247" data-mp="z3kfnl4v8gij">
                        Discount
                      </div>
                      <div className="col-span-3 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-4248" data-mp="17vcojre8rhl0">
                        Discount Type
                      </div>
                      <div className="col-span-1 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px] text-right" data-id="element-4249" data-mp="1hsqrqpb5m9vd">
                        Actions
                      </div>
                    </div>
                    <div className="divide-y divide-[#E2E8F0]" data-id="element-4250" data-mp="1qu71qvy249z2">
                      {pack.frequencies.map(freq => <FrequencyRow key={freq.id} freq={freq} customTiers={freqCustomTiers} onAddCustomTier={addCustomTier} onToggle={() => toggleFrequencyRow(pack.id, freq.id)} onUpdate={updates => updateFrequencyField(pack.id, freq.id, updates)} onDelete={() => deleteFrequency(pack.id, freq.id)} onDuplicate={() => duplicateFrequency(pack.id, freq.id)} data-id="element-4251" data-mp="3ojyl7b9fcv9b" />)}
                    </div>
                    <div className="px-4 py-3 bg-[#F8FAFC] border-t border-[#E2E8F0] flex items-center" data-id="element-4252" data-mp="3bbpr9j9imcd4">
                      <button className="flex items-center gap-1.5 text-[13px] font-medium text-[#475569] border border-dashed border-[#CBD5E1] hover:border-[#0EA5E9] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] px-3 py-1.5 rounded-md transition-colors" onClick={() => addFrequency(pack.id)} data-id="element-4253" data-mp="37ae8nxp26ref">
                        <Plus className="w-3.5 h-3.5" data-id="element-4254" data-mp="3uzozonglx7fd" />
                        Add Frequency
                      </button>
                    </div>
                  </div>}
                <div className="mt-4 flex items-center justify-between px-1 py-2 border-t border-[#E2E8F0]" data-id="element-4255" data-mp="3q0cbnze01ub7">
                  <button className="flex items-center gap-2 text-[13px] font-medium text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEF2F2] px-3 py-2 rounded-md transition-colors" onClick={() => deletePackSize(pack.id)} data-id="element-4256" data-mp="2jigysc5669fy">
                    <Trash2 className="w-4 h-4" data-id="element-4257" data-mp="mjnt8xrk3nq6" />
                    Delete Pack Size
                  </button>
                  <button className="flex items-center gap-2 text-[13px] font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] px-3 py-2 rounded-md transition-colors" onClick={() => duplicatePackSize(pack.id)} data-id="element-4258" data-mp="8ko1hh8qysps">
                    <Copy className="w-4 h-4" data-id="element-4259" data-mp="11a4tidsgg7lh" />
                    Duplicate Pack & Frequencies
                  </button>
                </div>
              </div>
            </div>}
        </Card>;
    })}

      <div className="flex items-center justify-between pt-2" data-id="element-4260" data-mp="ri7hj4tmhblp">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#E2E8F0] bg-white rounded-[6px] hover:border-[#CBD5E1] transition-colors" data-id="element-4261" data-mp="whtqh22f45op">
          <ArrowLeft className="w-4 h-4" data-id="element-4262" data-mp="1ozfu9v9t21zm" />
          Basics
        </button>
        <Button variant="primary" onClick={onNext} disabled={packSizes.length === 0} iconRight={<ArrowRight className="w-4 h-4" data-id="element-4264" data-mp="34pylpa0erg9l" />} data-id="element-4263" data-mp="39o9uriivpmqr">
          Next: Membership
        </Button>
      </div>
    </div>;
}